import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading'
import { MarkdownPreview } from '@/components/ui/markdown-preview'
import { useToast } from '@/hooks/use-toast'
import { Search, Sparkles, ChevronRight, Info } from 'lucide-react'
import { StaggerContainer, StaggerItem } from '@/components/animated/stagger-container'
import { InteractiveCard } from '@/components/animated/interactive-card'

interface SearchResult {
  id?: string | null
  question: string
  answer: string
  question_variations?: string[]
  category?: string
  problem_type?: string
  tags?: string[]
  escalation_rule?: string
  solution_content?: any
  followup_question_ids?: string[]
  followup_questions_text?: string[]
  similarity: number
  source_type?: 'knowledge' | 'import_source'
  source_uid?: string
  raw_content_preview?: string
}

interface VectorSearchProps {
  onSelectResult?: (result: SearchResult) => void
}

export function VectorSearch({ onSelectResult }: VectorSearchProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const { toast } = useToast()

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast({
        title: 'Fehler',
        description: 'Bitte geben Sie eine Suchanfrage ein',
        variant: 'destructive'
      })
      return
    }

    setIsSearching(true)
    setHasSearched(true)

    try {
      // Call the search_knowledge function from Supabase
      const { data, error } = await supabase.rpc('search_knowledge', {
        query_text: searchQuery,
        match_threshold: 0.7,
        match_count: 10
      })

      if (error) {
        console.error('Search error:', error)
        toast({
          title: 'Suchfehler',
          description: 'Die Suche konnte nicht durchgeführt werden',
          variant: 'destructive'
        })
        setSearchResults([])
        return
      }

      setSearchResults(data || [])

      if (data && data.length === 0) {
        toast({
          title: 'Keine Ergebnisse',
          description: 'Keine passenden FAQs gefunden. Versuchen Sie eine andere Suchanfrage.',
          variant: 'default'
        })
      }
    } catch (error) {
      console.error('Search error:', error)
      toast({
        title: 'Fehler',
        description: 'Ein unerwarteter Fehler ist aufgetreten',
        variant: 'destructive'
      })
    } finally {
      setIsSearching(false)
    }
  }

  const getSimilarityColor = (similarity: number) => {
    if (similarity > 0.9) return 'text-green-600'
    if (similarity > 0.8) return 'text-blue-600'
    if (similarity > 0.7) return 'text-yellow-600'
    return 'text-gray-600'
  }

  const getSimilarityLabel = (similarity: number) => {
    if (similarity > 0.9) return 'Sehr relevant'
    if (similarity > 0.8) return 'Relevant'
    if (similarity > 0.7) return 'Möglicherweise relevant'
    return 'Niedrige Relevanz'
  }

  return (
    <div className="space-y-6">
      {/* Search Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Intelligente Suche
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Beschreiben Sie Ihr Problem oder stellen Sie eine Frage..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button onClick={handleSearch} disabled={isSearching}>
                {isSearching ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-2">Suchen</span>
              </Button>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="h-4 w-4" />
              <span>
                Diese Suche nutzt KI-gestützte Vektorsuche für bessere Ergebnisse
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {isSearching ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : hasSearched && searchResults.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              Keine passenden Ergebnisse gefunden.<br />
              Versuchen Sie, Ihre Suchanfrage anders zu formulieren.
            </p>
          </CardContent>
        </Card>
      ) : searchResults.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {searchResults.length} Ergebnis{searchResults.length !== 1 ? 'se' : ''} gefunden
            </h3>
          </div>

          <StaggerContainer className="space-y-4">
            {searchResults.map((result, index) => (
              <StaggerItem key={result.id}>
                <InteractiveCard>
                  <Card
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => onSelectResult?.(result)}
                  >
                    <CardContent className="p-6">
                      <div className="space-y-3">
                        {/* Similarity Score */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`text-sm font-medium ${getSimilarityColor(result.similarity)}`}>
                              {(result.similarity * 100).toFixed(1)}% Übereinstimmung
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {getSimilarityLabel(result.similarity)}
                            </Badge>
                            {result.source_type === 'import_source' && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                Quelle
                              </Badge>
                            )}
                          </div>
                          {result.category && (
                            <Badge variant="secondary">
                              {result.category.replace('_', ' ')}
                            </Badge>
                          )}
                        </div>

                        {/* Question */}
                        <h4 className="font-semibold text-lg">
                          {result.question}
                        </h4>

                        {/* Answer Preview */}
                        <MarkdownPreview
                          content={result.answer}
                          maxLength={200}
                          className="line-clamp-3"
                        />

                        {/* Source UID for import sources */}
                        {result.source_type === 'import_source' && result.source_uid && (
                          <div className="pt-2 border-t">
                            <p className="text-xs text-muted-foreground">
                              Quellen-ID: <span className="font-mono">{result.source_uid}</span>
                            </p>
                          </div>
                        )}

                        {/* Question Variations if present */}
                        {result.question_variations && result.question_variations.length > 0 && (
                          <div className="pt-2">
                            <p className="text-xs text-muted-foreground mb-1">
                              Ähnliche Fragen:
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {result.question_variations.slice(0, 2).map((variation, idx) => (
                                <span key={idx} className="text-xs text-muted-foreground italic">
                                  "{variation}"
                                </span>
                              ))}
                              {result.question_variations.length > 2 && (
                                <span className="text-xs text-muted-foreground">
                                  +{result.question_variations.length - 2} mehr
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Follow-up Questions if present */}
                        {result.followup_questions_text && result.followup_questions_text.length > 0 && (
                          <div className="pt-2 border-t">
                            <p className="text-xs font-medium mb-2">
                              Folgefragen zur Diagnose:
                            </p>
                            <div className="space-y-1">
                              {result.followup_questions_text.slice(0, 3).map((question, idx) => (
                                <div key={idx} className="flex items-start gap-1">
                                  <ChevronRight className="h-3 w-3 mt-0.5 text-muted-foreground" />
                                  <span className="text-xs">{question}</span>
                                </div>
                              ))}
                              {result.followup_questions_text.length > 3 && (
                                <span className="text-xs text-muted-foreground italic">
                                  +{result.followup_questions_text.length - 3} weitere Fragen
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Tags */}
                        {result.tags && result.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 pt-2">
                            {result.tags.slice(0, 5).map(tag => (
                              <Badge key={tag} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* Escalation Rule */}
                        {result.escalation_rule && (
                          <div className="pt-2">
                            <Badge variant={
                              result.escalation_rule === 'self_solve' ? 'default' :
                              result.escalation_rule === 'mission_solar' ? 'secondary' : 'outline'
                            }>
                              Eskalation: {result.escalation_rule.replace('_', ' ')}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </InteractiveCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      ) : null}
    </div>
  )
}